import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import extraClassesService from '../services/extraClassesService';
import staffService from '../services/staffService';
import { ChalkboardTeacher, CurrencyCircleDollar, CheckCircle, XCircle } from '@phosphor-icons/react';

function formatCurrency(amount) {
  return `GHS ${Number(amount || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function titleCase(s) {
  return (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const PAYOUT_STATUS_COLORS = {
  unsubmitted: { color: '#6b7280', bg: '#f3f4f6' },
  pending: { color: '#d97706', bg: '#fef3c7' },
  verified: { color: '#16a34a', bg: '#f0fdf4' },
  rejected: { color: '#dc2626', bg: '#fef2f2' },
};

export default function ExtraClassesAdminPage() {
  const [tab, setTab] = useState('classes');
  const [classes, setClasses] = useState([]);
  const [pendingPayoutDetails, setPendingPayoutDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  const loadClasses = useCallback(() => {
    extraClassesService.getAdminOverview()
      .then((data) => setClasses(data.extra_classes || []))
      .catch((err) => toast.error(err.message || 'Failed to load extra classes overview'))
      .finally(() => setLoading(false));
  }, []);

  const loadPendingPayoutDetails = useCallback(() => {
    staffService.listPendingPayoutDetails()
      .then((data) => setPendingPayoutDetails(data.staff || []))
      .catch(() => setPendingPayoutDetails([]));
  }, []);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  useEffect(() => {
    if (tab === 'payout-verification') loadPendingPayoutDetails();
  }, [tab, loadPendingPayoutDetails]);

  const totals = classes.reduce(
    (acc, c) => ({
      revenue: acc.revenue + (c.total_revenue || 0),
      enrolled: acc.enrolled + (c.enrolled_count || 0),
    }),
    { revenue: 0, enrolled: 0 }
  );

  const handleVerifyPayout = async (staffId) => {
    try {
      setBusyId(staffId);
      await staffService.verifyStaffPayoutDetails(staffId);
      toast.success('Verified — a Paystack subaccount was created, payments will now settle directly to this teacher');
      loadPendingPayoutDetails();
      loadClasses();
    } catch (err) {
      toast.error(err.message || 'Unable to verify payout details');
    } finally {
      setBusyId(null);
    }
  };

  const handleRejectPayout = (staffId) => {
    setPromptDialog({
      title: 'Reject Payout Details',
      message: 'Reason for rejecting these payout details:',
      defaultValue: '',
      onSubmit: async (reason) => {
        try {
          setBusyId(staffId);
          await staffService.rejectStaffPayoutDetails(staffId, reason);
          toast.success('Rejected — the teacher can resubmit');
          loadPendingPayoutDetails();
        } catch (err) {
          toast.error(err.message || 'Unable to reject payout details');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  return (
    <Layout>
      <div className="page" style={{ display: 'grid', gap: 24 }}>
        <div className="page-header">
          <div>
            <h1>Extra Classes</h1>
            <p>School-wide oversight of teacher-led paid extra classes</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Total Revenue Collected</div>
            <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{formatCurrency(totals.revenue)}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Settled directly to teachers via Paystack — not held by the school</div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Total Enrolled Students</div>
            <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{totals.enrolled}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #e5e7eb' }}>
          <button className={`tab-btn ${tab === 'classes' ? 'active' : ''}`} onClick={() => setTab('classes')}>
            <ChalkboardTeacher size={16} /> Classes
          </button>
          <button className={`tab-btn ${tab === 'payout-verification' ? 'active' : ''}`} onClick={() => setTab('payout-verification')}>
            <CurrencyCircleDollar size={16} /> Payout Verification {pendingPayoutDetails.length > 0 && `(${pendingPayoutDetails.length})`}
          </button>
        </div>

        {tab === 'classes' && (
          <div className="card" style={{ padding: 0, overflow: 'auto' }}>
            {loading ? (
              <div style={{ padding: 20, color: '#6b7280' }}>Loading...</div>
            ) : classes.length === 0 ? (
              <div style={{ padding: 20, color: '#6b7280' }}>No extra classes yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                    <th style={{ padding: 12 }}>Class</th>
                    <th style={{ padding: 12 }}>Teacher</th>
                    <th style={{ padding: 12 }}>Status</th>
                    <th style={{ padding: 12 }}>Teacher Payout</th>
                    <th style={{ padding: 12 }}>Price</th>
                    <th style={{ padding: 12 }}>Enrolled</th>
                    <th style={{ padding: 12 }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((c) => {
                    const payoutMeta = PAYOUT_STATUS_COLORS[c.teacher_payout_status] || PAYOUT_STATUS_COLORS.unsubmitted;
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: 12, fontWeight: 600 }}>{c.title}</td>
                        <td style={{ padding: 12 }}>{c.teacher_name || 'Unknown'}</td>
                        <td style={{ padding: 12, textTransform: 'capitalize' }}>{(c.status || '').replace('_', ' ')}</td>
                        <td style={{ padding: 12 }}>
                          <span style={{ fontSize: 12, color: payoutMeta.color, background: payoutMeta.bg, padding: '2px 8px', borderRadius: 8, textTransform: 'capitalize' }}>
                            {c.teacher_payout_status || 'unsubmitted'}
                          </span>
                        </td>
                        <td style={{ padding: 12 }}>{formatCurrency(c.price)} / {c.billing_interval}</td>
                        <td style={{ padding: 12 }}>{c.enrolled_count}/{c.max_students_per_session}</td>
                        <td style={{ padding: 12, fontWeight: 600 }}>{formatCurrency(c.total_revenue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'payout-verification' && (
          <div style={{ display: 'grid', gap: 12 }}>
            {pendingPayoutDetails.length === 0 ? (
              <div className="card" style={{ padding: 20, color: '#6b7280' }}>Nothing waiting for verification.</div>
            ) : (
              pendingPayoutDetails.map((s) => (
                <div key={s.id} className="card" style={{ padding: 16 }}>
                  <div style={{ fontWeight: 700 }}>{s.name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{s.position}</div>
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>
                    <strong>{s.payout_account_name}</strong> — {titleCase(s.payout_account_type)} · {s.payout_account_number}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                    Name resolved directly from the bank/mobile money provider — not typed in by the teacher.
                  </div>
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ padding: '6px 14px' }} disabled={busyId === s.id} onClick={() => handleVerifyPayout(s.id)}>
                      <CheckCircle size={14} /> Verify
                    </button>
                    <button className="btn btn-danger" style={{ padding: '6px 14px' }} disabled={busyId === s.id} onClick={() => handleRejectPayout(s.id)}>
                      <XCircle size={14} /> Reject
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="info"
        input={{ placeholder: 'Reason', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}
